"use client"

import { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { ChevronRight } from "lucide-react"
import { useApp } from "@/lib/context"
import { categoryIcon } from "@/lib/category-icons"
import { effectivePrice, type Dish } from "@/lib/api"
import DishDetailModal from "@/components/dish-detail-modal"
import { scrollToSection } from "./MenuHero"

function formatPrice(price: number) {
    return new Intl.NumberFormat("pt-AO", {
        style: "currency",
        currency: "AOA",
        minimumFractionDigits: 0,
    }).format(price)
}

export default function ElegantCategoriesSection() {
    const { categories, t, buffete } = useApp()
    const [selectedDish, setSelectedDish] = useState<Dish | null>(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [activeChip, setActiveChip] = useState(0)

    const anchors = useMemo(
        () => categories.map((category, index) => ({
            id: `categoria-${index}`,
            nome: category.nome,
        })),
        [categories],
    )

    // Marca o chip da categoria visível enquanto o cliente rola
    useEffect(() => {
        if (!anchors.length) return
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const index = anchors.findIndex((a) => a.id === entry.target.id)
                        if (index >= 0) setActiveChip(index)
                    }
                }
            },
            { rootMargin: "-30% 0px -60% 0px" },
        )
        anchors.forEach(({ id }) => {
            const el = document.getElementById(id)
            if (el) observer.observe(el)
        })
        return () => observer.disconnect()
    }, [anchors])

    const handleDishClick = (dish: Dish) => {
        setSelectedDish(dish)
        setShowDetailModal(true)
    }

    if (!categories || categories.length === 0) {
        return null
    }

    return (
        <section className="bg-background">
            {/* Navegação fixa por categoria — rolável na horizontal */}
            <nav
                aria-label="Categorias do menu"
                className="sticky top-0 z-40 border-y border-border bg-background/90 backdrop-blur-md"
            >
                <div className="hide-scrollbar snap-row flex gap-2 overflow-x-auto px-4 py-2.5 sm:px-6">
                    {anchors.map(({ id, nome }, index) => {
                        const Icon = categoryIcon(nome)
                        const active = activeChip === index
                        return (
                            <button
                                key={id}
                                onClick={() => {
                                    setActiveChip(index)
                                    scrollToSection(id)
                                }}
                                aria-current={active ? "true" : undefined}
                                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[0.7rem] uppercase tracking-wider transition-colors ${
                                    active
                                        ? "border-primary bg-primary text-primary-foreground"
                                        : "border-primary/25 text-foreground/70 hover:border-primary/50 hover:text-foreground"
                                }`}
                            >
                                <Icon className="size-3.5 shrink-0" aria-hidden="true" />
                                <span className="whitespace-nowrap">{nome}</span>
                            </button>
                        )
                    })}
                    <button
                        onClick={() => scrollToSection("bebidas")}
                        className="flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[0.7rem] uppercase tracking-wider transition-colors"
                        style={{
                            borderColor: "hsl(var(--drink) / 0.4)",
                            color: "hsl(var(--drink))",
                        }}
                    >
                        Bebidas
                    </button>
                    {buffete && (
                        <button
                            onClick={() => scrollToSection("sec-bufete")}
                            className="flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[0.7rem] font-semibold uppercase tracking-wider transition-colors"
                            style={{
                                borderColor: "hsl(var(--buffet) / 0.5)",
                                color: "hsl(var(--buffet))",
                            }}
                        >
                            Bufete
                        </button>
                    )}
                </div>
            </nav>

            <div className="mx-auto max-w-7xl px-0 sm:px-2">
                {categories.map((category, catIndex) => {
                    const Icon = categoryIcon(category.nome)
                    const accent = "var(--primary)"
                    const sectionId = `categoria-${catIndex}`

                    return (
                        <div
                            key={catIndex}
                            id={sectionId}
                            className="scroll-mt-16 py-7 sm:py-9"
                        >
                            {/* Cabeçalho compacto */}
                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.45 }}
                                className="mb-4 flex items-center gap-3 px-4 sm:px-6"
                            >
                                <span
                                    className="flex size-9 shrink-0 items-center justify-center rounded-lg border"
                                    style={{
                                        borderColor: `hsl(${accent} / 0.35)`,
                                        background: `hsl(${accent} / 0.08)`,
                                        color: `hsl(${accent})`,
                                    }}
                                >
                                    <Icon className="size-4" aria-hidden="true" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <h3 className="truncate font-serif text-xl font-light tracking-wide text-foreground sm:text-2xl">
                                        {category.nome}
                                    </h3>
                                    {category.descricao && (
                                        <p className="truncate text-xs font-light text-foreground/50">
                                            {category.descricao}
                                        </p>
                                    )}
                                </div>
                                <span className="shrink-0 text-xs font-light uppercase tracking-wider text-foreground/40">
                                    {category.pratos.length} {t("items")}
                                </span>
                            </motion.div>

                            {/* Carrossel horizontal de pratos */}
                            <div className="hide-scrollbar snap-row flex gap-3 overflow-x-auto px-4 pb-1 sm:px-6">
                                {category.pratos.map((dish) => (
                                    <motion.button
                                        key={dish._id}
                                        initial={{ opacity: 0, y: 16 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.35 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleDishClick(dish)}
                                        className="group w-56 shrink-0 overflow-hidden border border-border bg-card text-left transition-colors hover:border-primary/45 sm:w-64"
                                    >
                                        {dish.urls && dish.urls.length > 0 ? (
                                            <div className="relative aspect-[5/3] overflow-hidden">
                                                <img
                                                    src={dish.urls[0]}
                                                    alt={dish.nome}
                                                    loading="lazy"
                                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    onError={(e) => {
                                                        e.currentTarget.src = "/placeholder.svg"
                                                    }}
                                                />
                                                {dish.destacado && (
                                                    <span className="absolute left-2 top-2 bg-primary px-2 py-0.5 text-[0.6rem] uppercase tracking-widest text-primary-foreground">
                                                        Destaque
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex aspect-[5/3] items-center justify-center bg-secondary">
                                                <Icon
                                                    className="size-8 text-primary/30"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        )}

                                        <div className="p-3">
                                            <h4 className="truncate font-serif text-base font-light text-foreground group-hover:text-primary">
                                                {dish.nome}
                                            </h4>
                                            {dish.descricao && (
                                                <p className="mt-0.5 line-clamp-1 text-xs font-light text-foreground/50">
                                                    {dish.descricao}
                                                </p>
                                            )}

                                            <div className="mt-2.5 flex items-center justify-between">
                                                <span className="flex items-baseline gap-1.5">
                                                    {dish.emPromocao && dish.precoPromocional != null && (
                                                        <span className="text-xs font-light text-foreground/40 line-through">
                                                            {formatPrice(dish.preco)}
                                                        </span>
                                                    )}
                                                    <span
                                                        className="font-serif text-lg"
                                                        style={{ color: `hsl(${accent})` }}
                                                    >
                                                        {formatPrice(effectivePrice(dish))}
                                                    </span>
                                                </span>
                                                <ChevronRight
                                                    className="size-4 text-primary/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    )
                })}
            </div>

            {selectedDish && (
                <DishDetailModal
                    dish={selectedDish}
                    isOpen={showDetailModal}
                    onClose={() => {
                        setShowDetailModal(false)
                        setTimeout(() => setSelectedDish(null), 300)
                    }}
                />
            )}
        </section>
    )
}
